import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../classes/util';
import { BooleanConverter, InputConverter } from '../../inputConverter';
import { AreaUnits, DistanceUnits, FillStyleOptions, LineStyleOptions } from '../../selectionOptions';
import { BufferService } from '../../services/buffer.service';
import { ILoggingService } from '../../services/interfaces/ILoggingService';
import { LayerService } from '../../services/layer.service';
import { SpatialQueryService } from '../../services/spatialQuery.service';
import { LogLevel, MapCoreLayer } from '../../types';
import { BaseComponent } from '../base.component';

import { BufferOptions } from './buffer.config';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-buffer',
    styleUrls: ['../sharedStyles/base.scss', '../sharedStyles/telerik.scss', './buffer.component.scss'],
    templateUrl: './buffer.component.html'
})
export class BufferComponent extends BaseComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() mapView: NonNullable<__esri.MapView>;
    @Input() mapLayers: any[];
    @Input() options: any;
    @Input() createBuffer = this.buffer.bind(this);
    @Input() @InputConverter(BooleanConverter) showOnlyApplicationUserMaps: boolean = false;

    @Input()
    set active(value: boolean) {
        this._active = BooleanConverter(value);
        this._active ? this.activate() : this.deactivate();
    }
    get active(): boolean { return this._active; }
    private _active: boolean = false;

    @Input()
    set geometry(value: __esri.Geometry | __esri.Geometry[]) {
        this._geometry = value;
        if (value) {
            this._layer = null;
        }
    }
    get geometry(): __esri.Geometry | __esri.Geometry[] { return this._geometry; }
    private _geometry: __esri.Geometry | __esri.Geometry[];

    @Input()
    set layer(value: MapCoreLayer) {
        this._layer = value;
        if (value) {
            this._geometry = null;
        }
    }
    get layer(): MapCoreLayer { return this._layer; }
    private _layer: any;

    @Input()
    showSettings = {
        type: true,
        distance: true,
        distanceUnit: true,
        areaUnit: true,
        fillType: true,
        fillColor: true,
        outlineType: true,
        outlineColor: true
    };

    @Output() readonly buffered = new EventEmitter<boolean>();

    distanceUnits;
    areaUnits;
    areaUnitOptionLabel;
    fillTypes;
    fillColors: string[];
    outlineTypes;
    outlineColors: string[];
    bufferOptions: BufferOptions;
    bufferDistance: number | string;
    bufferableLayers: MapCoreLayer[];
    selectedLayers: any[];
    buffering = false;
    warningMsg: string = null;
    warningLimit: number = 4;

    set multiBuffer(value: string) {
        this._multiBuffer = value;
        if (value === 'false') {
            this.bufferDistance = this.bufferDistance.toString().split(',')[0];
        }
    }
    get multiBuffer(): string { return this._multiBuffer; }
    private _multiBuffer: string = 'false';

    constructor(
        _vcr: ViewContainerRef,
        private readonly bufferService: BufferService,
        private readonly layerService: LayerService,
        private readonly spatialQueryService: SpatialQueryService,
        private readonly cdr: ChangeDetectorRef,
        @Inject('LoggingService') public readonly LoggingService: ILoggingService) {
        super(_vcr, true);
    }

    fillColorPicker: ViewContainerRef;
    @ViewChild('fillColorPicker') set fillColorPickerRef(el: ViewContainerRef) { this.fillColorPicker = el; }
    outlineColorPicker: ViewContainerRef;
    @ViewChild('outlineColorPicker') set outlineColorPickerRef(el: ViewContainerRef) { this.outlineColorPicker = el; }

    async ngOnInit() {
        this.bufferOptions = util.deepClone(this.bufferService.getDefaultBufferOptions());
        this.distanceUnits = DistanceUnits;
        this.areaUnits = [...AreaUnits];
        this.areaUnits.unshift({
            name: 'Select a unit...',
            value: null
        });
        this.fillTypes = FillStyleOptions;
        this.outlineTypes = LineStyleOptions;
        this.bufferDistance = this.bufferOptions.distance.toString();
    }

    async ngAfterViewInit() {
        if (!this.mapView) {
            this.mapView = await this.setupMapViewRef(this.mapView);
            this.isReady();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.mapLayers) {
            this.bufferableLayers = this.getBufferableLayers(this.mapView.map, changes.mapLayers.currentValue);
        }
    }

    public checkFilteredLayerWarning() {
        const newBufferCount = this.bufferDistance.toString().split(',').length;
        const newFilteredLayerCount = newBufferCount * (this.selectedLayers ? this.selectedLayers.length : 0);
        if (newBufferCount > 1 && newFilteredLayerCount > this.warningLimit) {
            this.warningMsg = `Map will have ${newFilteredLayerCount} new filtered layers added. One for each combination of layer and buffer selected. Additional processing time may be required.`;
        } else {
            this.warningMsg = null;
        }
    }

    private activate() {
        this.bufferableLayers = this.getBufferableLayers(this.mapView.map, this.mapLayers);
        this.selectedLayers = [];
        this.warningMsg = null;
    }

    private deactivate() {
        this.layer = null;
        this.geometry = null;
    }

    isMultiBuffer() {
        return this.multiBuffer === 'true';
    }

    private getBufferableLayers(map: __esri.Map, requestedLayers?: any[]): MapCoreLayer[] {
        // Host may pass in requestedLayers with: layers/ids, empty list or no list.
        // We then: find matching layers, hide dropdown, or show all appropriate layers respectively.
        return (<MapCoreLayer[]><unknown>map.layers.toArray()).filter(x => {
            return (!requestedLayers || requestedLayers.find(y => y.id === x.id))
                && (!this.layer || this.layer.id !== x.id)
                && !x.mapcoreProps.isAOI
                && !this.layerService.isTempLayer(x)
                && this.layerService.getLayerType(x) !== 'graphics'; // No graphic layer support yet
        }).sort((a, b) => a.mapcoreProps.name.localeCompare(b.mapcoreProps.name));
    }

    async buffer() {

        let bufferCompleted = false;
        this.buffering = true;
        this.warningMsg = null;

        try {
            this.bufferOptions.distance = this.bufferDistance.toString().split(',').map(d => Number(d.trim()));
            let buffers;
            if (this.layer) {
                this.bufferOptions.unionResults = true;
                const results = await this.bufferService.getLayerBuffers(this.layer, this.bufferOptions);
                if (results && results.length) {
                    if (!results[0].featureCount) {
                        this.warningMsg = 'Selected layer has no features to buffer.';
                    } else if (results[0].featureCount > this.bufferService.bufferFeatureLimit) {
                        this.warningMsg = `Selected layer has greater than ${this.bufferService.bufferFeatureLimit} features. Please filter layer to ${this.bufferService.bufferFeatureLimit} or less features before buffering.`;
                    }
                    buffers = results[0].buffers;
                }
            } else if (this.geometry) {
                buffers = await this.bufferService.getGeometryBuffers(this.geometry, this.bufferOptions);
            }
            if (buffers.length) {
                const graphicsLayers = await this.bufferService.addBufferToMap(buffers, this.bufferOptions);
                if (this.selectedLayers && this.selectedLayers.length) {
                    graphicsLayers.forEach(async (bufferLayer) => {
                        const joinedGeometries = [];
                        this.spatialQueryService.filterSelectedLayers([bufferLayer], [], [], joinedGeometries);
                        const results: [{ id: string; guid: string }] = await this.spatialQueryService.spatialQueryLayers(true, this.selectedLayers, graphicsLayers);
                        const spatialQueryParams = {
                            primaryDynamicLayers: [],
                            primaryFeatureLayers: [],
                            joinedDynamicLayers: [],
                            joinedFeatureLayers: [],
                            joinedGeometries,
                            isIntersect: true,
                            guid: ''
                        };
                        const title = `intersected by ${bufferLayer.title}`;
                        await this.spatialQueryService.handleResults(results, spatialQueryParams, true, true, '', title);
                    });
                }
                bufferCompleted = true;
            }
        }
        catch (error) {
            this.LoggingService.Log('Error creating or applying buffer', error, LogLevel.Error);
        }
        finally {
            this.buffering = false;
            this.buffered.emit(bufferCompleted);
        }
        this.cdr.detectChanges();
    }
}
